import { useEffect, useState } from 'react';
import { cloudStorage } from '@telegram-apps/sdk-react';

import { GeolocationCloudKeys } from '@/enums/storageEnums.ts';

export const BROWSER_GEO_LIFETIME = 3 * 24 * 60 * 60 * 1000; // 3 days in milliseconds

interface Coordinates {
  latitude: number;
  longitude: number;
  accuracy: number;
}

interface GeolocationError {
  code: number;
  message: string;
}

export const useBrowserGeolocationControl = (
  isTelegramGeoSupported: boolean
) => {
  const [position, setPosition] = useState<Coordinates | null>(null);
  const [error, setError] = useState<GeolocationError | null>(null);
  const [loading, setLoading] = useState<boolean>(true);

  useEffect(() => {
    if (isTelegramGeoSupported) {
      setLoading(false);
      return;
    }

    if (!navigator.geolocation) {
      setLoading(false);
      setError({
        code: 0,
        message: 'Geolocation is not supported by this browser.'
      });
      return;
    }

    const checkAndRequestLocation = async () => {
      let lastBrowserRequestedAt: number | null = null;
      if (cloudStorage.getItem.isAvailable()) {
        const storedValue = await cloudStorage.getItem(
          GeolocationCloudKeys.BROWSER_GEO_KEY
        );
        if (storedValue) {
          const parsedDate = new Date(storedValue);
          const parsedTimestamp = parsedDate.getTime();
          if (!isNaN(parsedTimestamp) && parsedTimestamp > 0) {
            lastBrowserRequestedAt = parsedTimestamp;
          }
        }
      }
      // Check if request was made within last BROWSER_GEO_LIFETIME
      if (lastBrowserRequestedAt !== null) {
        const now = Date.now();

        if (now - lastBrowserRequestedAt < BROWSER_GEO_LIFETIME) {
          setLoading(false);
          return;
        }
      }

      const options = {
        enableHighAccuracy: true,
        timeout: 5000,
        maximumAge: 60000
      };

      const success = (pos: GeolocationPosition) => {
        const crd = pos.coords;
        setPosition({
          latitude: crd.latitude,
          longitude: crd.longitude,
          accuracy: crd.accuracy
        });
        setLoading(false);

        if (cloudStorage.setItem.isAvailable()) {
          const now = new Date().toISOString();
          void cloudStorage.setItem(GeolocationCloudKeys.BROWSER_GEO_KEY, now);
        }
      };

      const errors = (err: GeolocationPositionError) => {
        setError({
          code: err.code,
          message: err.message
        });
        setLoading(false);

        if (cloudStorage.setItem.isAvailable()) {
          const now = new Date().toISOString();
          void cloudStorage.setItem(GeolocationCloudKeys.BROWSER_GEO_KEY, now);
        }
      };

      if (navigator.permissions && navigator.permissions.query) {
        try {
          const result = await navigator.permissions.query({
            name: 'geolocation'
          });
          if (result.state === 'granted' || result.state === 'prompt') {
            navigator.geolocation.getCurrentPosition(success, errors, options);
          } else if (result.state === 'denied') {
            setError({ code: 0, message: 'Location access denied.' });
            setLoading(false);

            if (cloudStorage.setItem.isAvailable()) {
              const now = new Date().toISOString();
              void cloudStorage.setItem(
                GeolocationCloudKeys.BROWSER_GEO_KEY,
                now
              );
            }
          }
        } catch {
          navigator.geolocation.getCurrentPosition(success, errors, options);
        }
      } else {
        navigator.geolocation.getCurrentPosition(success, errors, options);
      }
    };

    void checkAndRequestLocation();

    return () => {
      // Handle any cleanup if necessary
    };
  }, [isTelegramGeoSupported]);

  return { position, error, loading };
};
